<script setup>
import { computed } from 'vue';

const props = defineProps({
  values: {
    type: Array,
    default: () => [],
  },
  avgValue: {
    type: [Number, String],
    default: null,
  },
  yMin: {
    type: Number,
    default: 0,
  },
  yMax: {
    type: Number,
    default: 100,
  },
  sparklineClass: {
    type: String,
    default: 'text-n-brand',
  },
  edgeToEdge: {
    type: Boolean,
    default: false,
  },
  compact: {
    type: Boolean,
    default: false,
  },
});

const padX = computed(() => (props.edgeToEdge ? 0 : 8));
const padY = 10;

const VB_W = 320;
const VB_H = 88;

const valueToY = value => {
  const range = props.yMax - props.yMin || 1;
  const normalized = (Number(value) - props.yMin) / range;
  return padY + (1 - normalized) * (VB_H - padY * 2);
};

const mapPoints = values => {
  if (!values.length) return [];

  const xPad = padX.value;

  if (values.length === 1) {
    const y = valueToY(values[0]);
    return [
      { x: xPad, y },
      { x: VB_W - xPad, y },
    ];
  }

  return values.map((value, index) => ({
    x: xPad + (index / (values.length - 1)) * (VB_W - xPad * 2),
    y: valueToY(value),
  }));
};

const buzzPoints = computed(() =>
  props.values.filter(value => value != null).map(value => Number(value))
);

const points = computed(() => mapPoints(buzzPoints.value));

const linePath = computed(() => {
  if (!points.value.length) return '';
  return points.value
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ');
});

const areaPath = computed(() => {
  if (!points.value.length) return '';
  const first = points.value[0];
  const last = points.value[points.value.length - 1];
  return `${linePath.value} L ${last.x} ${VB_H} L ${first.x} ${VB_H} Z`;
});

const avgLinePath = computed(() => {
  if (props.avgValue == null || props.avgValue === '' || !points.value.length) {
    return '';
  }
  const y = valueToY(props.avgValue);
  const startX = points.value[0].x;
  const endX = points.value[points.value.length - 1].x;
  return `M ${startX} ${y} L ${endX} ${y}`;
});

const hasChart = computed(() => points.value.length > 0);
</script>

<template>
  <svg
    v-if="hasChart"
    class="absolute inset-x-0 bottom-0 w-full pointer-events-none"
    :class="[sparklineClass, compact ? 'h-10' : 'top-[30%] h-full']"
    viewBox="0 0 320 88"
    preserveAspectRatio="none"
    aria-hidden="true"
  >
    <path :d="areaPath" class="fill-current opacity-25" />
    <path
      :d="linePath"
      class="fill-none stroke-current opacity-60"
      stroke-width="1.5"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      v-if="avgLinePath"
      :d="avgLinePath"
      class="fill-none stroke-n-slate-9 opacity-50"
      stroke-width="1"
      stroke-dasharray="4 4"
      stroke-linecap="round"
    />
  </svg>
</template>
